/**
 * RTK Query API Slice for Report Generation and Downloads
 * 
 * Handles all report-related endpoints including:
 * - Single report generation (PDF, CSV, JSON)
 * - Batch report generation for multiple recordings
 * - File download management
 * - Recording exports and data exports
 * - Dashboard data export functionality
 * - Progress tracking for large report operations
 * 
 * Key architectural decisions:
 * - Uses existing apiClient as baseQuery (maintains snake_case)
 * - Handles binary data downloads (PDFs, CSVs)
 * - Progress tracking for batch operations
 * - Streaming downloads for large files
 * - Appropriate caching for generated reports
 * - Support for multiple export formats
 * - Memory-efficient file handling
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import apiClientBaseQuery from './baseQuery';
import apiClient from '../../helpers/apiClient';

// Custom base query for file downloads that handles binary data
const reportBaseQuery = async (args, api, extraOptions) => {
  // For download endpoints, we need to handle binary responses differently
  if (args.responseType === 'blob' || (args.url && (args.url.includes('/download/') || args.url.includes('generate_batch_report')))) {
    try {
      // Use apiClient's axios instance for binary downloads
      const response = await apiClient({
        url: args.url,
        method: args.method || 'GET',
        responseType: 'blob',
        headers: args.headers,
        data: args.data,
      });

      // Axios returns data directly, not a Response object
      const blob = response.data;
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'download';
      
      if (contentDisposition) {
        // More robust filename extraction that handles both quoted and unquoted filenames
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }

      return {
        data: {
          blob,
          url: window.URL.createObjectURL(blob),
          filename,
          contentType: response.headers.get('Content-Type'),
          size: blob.size,
        },
      };
    } catch (error) {
      return {
        error: {
          status: error.status || 500,
          data: { message: error.message || 'Download failed' },
        },
      };
    }
  }

  // Use standard apiClient for non-binary responses
  return apiClientBaseQuery(args, api, extraOptions);
};

export const reportApi = createApi({
  reducerPath: 'reportApi',
  baseQuery: reportBaseQuery,
  
  // Report generation and downloads have specific caching needs
  keepUnusedDataFor: 600, // 10 minutes for generated reports
  refetchOnMountOrArgChange: false, // Don't refetch generated reports unnecessarily
  refetchOnFocus: false,
  
  // Define cache tags for invalidation
  tagTypes: ['Report', 'ReportJob', 'Export', 'Download'],
  
  endpoints: (builder) => ({
    // Generate single report
    generateReport: builder.mutation({
      query: (reportData) => ({
        url: '/v1/generate_report',
        method: 'POST',
        data: {
          // Recording and user information
          recording_id: reportData.recording_id,
          user_id: reportData.user_id,
          client_id: reportData.client_id,
          
          // Report configuration
          report_type: reportData.report_type || 'standard', // standard, detailed, summary
          output_format: reportData.output_format || 'pdf', // pdf, html, json
          
          // Content options
          include_raw_data: reportData.include_raw_data || false,
          include_charts: reportData.include_charts !== false, // Default true
          include_metrics_table: reportData.include_metrics_table !== false,
          include_analysis: reportData.include_analysis !== false,
          include_recommendations: reportData.include_recommendations || false,
          
          // Template and styling
          template_id: reportData.template_id,
          custom_branding: reportData.custom_branding || false,
          logo_url: reportData.logo_url,
          header_text: reportData.header_text,
          footer_text: reportData.footer_text,
          
          // Metrics selection
          metric_ids: reportData.metric_ids || [], // Specific metrics to include
          exclude_metrics: reportData.exclude_metrics || [],
          
          // Date range for trends (if applicable)
          date_range: reportData.date_range,
          comparison_period: reportData.comparison_period,
          
          // Language and localization
          language: reportData.language || 'en',
          timezone: reportData.timezone,
          units: reportData.units || 'metric', // metric, imperial
          
          // Advanced options
          resolution: reportData.resolution || 'standard', // low, standard, high
          color_scheme: reportData.color_scheme || 'default',
          page_orientation: reportData.page_orientation || 'portrait',
          page_size: reportData.page_size || 'A4',
        },
      }),
      transformResponse: (response) => {
        // Single report generation returns download URL or job ID
        return response.data || {};
      },
      keepUnusedDataFor: 300, // 5 minutes for single reports
      providesTags: (result, error, args) => [
        { type: 'Report', id: args.recording_id }
      ],
    }),

    // Generate batch report for multiple recordings
    generateBatchReport: builder.mutation({
      query: (batchData) => {
        // Map frontend field names to backend expectations
        const requestData = {
          // The backend expects 'query' not 'recording_ids'
          query: batchData.recording_ids || batchData.query,
          // Template ID with default
          template_id: batchData.template_id || 1,
          // User ID from params or will be extracted from JWT
          user_id: batchData.user_id,
          // Output format - NEVER pdf, only xlsx or csv
          output_format: batchData.format || batchData.output_format || 'xlsx',
          // Direct streaming by default
          use_direct: true,
        };

        // Remove any undefined values
        Object.keys(requestData).forEach(key => {
          if (requestData[key] === undefined) {
            delete requestData[key];
          }
        });

        return {
          url: '/v1/generate_batch_report', // Leading slash required for correct routing
          method: 'POST',
          data: requestData,
          responseType: 'blob', // This tells the custom base query to handle it as a file download
        };
      },
      transformResponse: (response) => {
        // The custom base query returns blob data with metadata
        if (response.blob) {
          // Trigger file download
          const link = document.createElement('a');
          link.href = response.url;
          link.download = response.filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          // Clean up the blob URL after download
          setTimeout(() => {
            window.URL.revokeObjectURL(response.url);
          }, 100);
          
          return { success: true, filename: response.filename };
        }
        return response;
      },
      keepUnusedDataFor: 0, // Don't cache batch operations
    }),

    // Download generated report
    downloadReport: builder.mutation({
      query: (downloadData) => ({
        url: `/download/${downloadData.id}`,
        method: 'GET',
        responseType: 'blob',
        params: {
          format: downloadData.format,
          attachment: downloadData.attachment !== false, // Default true
        },
      }),
      transformResponse: (response) => {
        // File download handling is done in custom base query
        return response;
      },
    }),

    // Get report generation status (for batch reports)
    getReportStatus: builder.query({
      query: (jobId) => ({
        url: `/reports/status/${jobId}`,
        method: 'GET',
      }),
      transformResponse: (response) => {
        return response.data || { 
          status: 'unknown', 
          progress: 0, 
          total_reports: 0, 
          completed_reports: 0 
        };
      },
      keepUnusedDataFor: 0,
      providesTags: (result, error, jobId) => [
        { type: 'ReportJob', id: jobId }
      ],
    }),

    // Batch download recordings
    batchDownloadRecordings: builder.mutation({
      query: (downloadData) => ({
        url: '/recordings/batch-download',
        method: 'POST',
        data: {
          recording_ids: downloadData.recording_ids,
          format: downloadData.format || 'json', // json, csv, raw
          compression: downloadData.compression || 'zip',
          include_metadata: downloadData.include_metadata !== false,
          include_analysis: downloadData.include_analysis || false,
          date_range: downloadData.date_range,
          client_id: downloadData.client_id,
          user_ids: downloadData.user_ids,
        },
      }),
      transformResponse: (response) => {
        return response.data || {};
      },
    }),

    // Export recordings with advanced filtering
    exportRecordings: builder.mutation({
      query: (exportData) => ({
        url: '/recordings/export',
        method: 'POST',
        data: {
          // Filtering options
          client_id: exportData.client_id,
          user_ids: exportData.user_ids,
          start_date: exportData.start_date,
          end_date: exportData.end_date,
          recording_ids: exportData.recording_ids,
          
          // Export format and options
          format: exportData.format || 'csv', // csv, json, xlsx
          include_headers: exportData.include_headers !== false,
          include_metadata: exportData.include_metadata !== false,
          include_metrics: exportData.include_metrics !== false,
          include_raw_data: exportData.include_raw_data || false,
          
          // Field selection
          fields: exportData.fields || [], // Specific fields to export
          metric_fields: exportData.metric_fields || [],
          
          // Formatting options
          date_format: exportData.date_format || 'ISO8601',
          decimal_places: exportData.decimal_places || 2,
          null_value: exportData.null_value || '',
          
          // Advanced options
          flatten_json: exportData.flatten_json !== false, // For JSON exports
          compression: exportData.compression, // For large exports
          chunk_size: exportData.chunk_size || 1000,
        },
      }),
      transformResponse: (response) => {
        return response.data || {};
      },
    }),

    // Export dashboard data
    exportDashboardData: builder.mutation({
      query: (exportData) => ({
        url: '/dashboard/export-data',
        method: 'POST',
        data: {
          // Dashboard context
          client_id: exportData.client_id,
          user_id: exportData.user_id,
          template_id: exportData.template_id,
          
          // Date range
          start_date: exportData.start_date,
          end_date: exportData.end_date,
          
          // Export configuration
          format: exportData.format || 'csv',
          include_charts: exportData.include_charts || false,
          include_trends: exportData.include_trends || false,
          include_comparisons: exportData.include_comparisons || false,
          
          // Metric selection
          metric_ids: exportData.metric_ids || [],
          aggregation_level: exportData.aggregation_level || 'daily', // hourly, daily, weekly, monthly
          
          // Filtering
          slicer_filters: exportData.slicer_filters || {},
          exclude_outliers: exportData.exclude_outliers || false,
          
          // Formatting
          include_norms: exportData.include_norms || false,
          include_percentiles: exportData.include_percentiles || false,
          group_by: exportData.group_by || [], // user, date, metric
        },
      }),
      transformResponse: (response) => {
        return response.data || {};
      },
    }),

    // Get available report templates
    getReportTemplates: builder.query({
      query: (params = {}) => ({
        url: '/reports/templates',
        method: 'GET',
        params: {
          client_id: params.client_id,
          type: params.type, // single, batch, dashboard
          category: params.category,
        },
      }),
      transformResponse: (response) => {
        return response.data || [];
      },
      // Templates can be cached
      keepUnusedDataFor: 600, // 10 minutes
      providesTags: ['ReportTemplates'],
    }),

    // Get report history for user/client
    getReportHistory: builder.query({
      query: (params = {}) => ({
        url: '/reports/history',
        method: 'GET',
        params: {
          user_id: params.user_id,
          client_id: params.client_id,
          start_date: params.start_date,
          end_date: params.end_date,
          report_type: params.report_type,
          status: params.status, // completed, failed, processing
          page: params.page || 1,
          limit: params.limit || 50,
        },
      }),
      transformResponse: (response) => {
        return response.data || { reports: [], total: 0 };
      },
      keepUnusedDataFor: 300, // 5 minutes
    }),

    // Cancel report generation job
    cancelReportJob: builder.mutation({
      query: (jobId) => ({
        url: `/reports/cancel/${jobId}`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, jobId) => [
        { type: 'ReportJob', id: jobId }
      ],
    }),

    // Get report configuration options
    getReportConfig: builder.query({
      query: (params = {}) => ({
        url: '/reports/config',
        method: 'GET',
        params: {
          client_id: params.client_id,
          report_type: params.report_type,
        },
      }),
      transformResponse: (response) => {
        return response.data || {
          available_formats: [],
          available_templates: [],
          max_batch_size: 100,
          supported_metrics: [],
        };
      },
      // Config can be cached
      keepUnusedDataFor: 600, // 10 minutes
    }),

    // Preview report before generation
    previewReport: builder.mutation({
      query: (previewData) => ({
        url: '/reports/preview',
        method: 'POST',
        data: {
          recording_id: previewData.recording_id,
          template_id: previewData.template_id,
          report_type: previewData.report_type || 'standard',
          format: 'html', // Always HTML for preview
          include_charts: previewData.include_charts !== false,
          include_sample_data: true,
        },
      }),
      transformResponse: (response) => {
        return response.data || { html: '', estimated_size: 0 };
      },
    }),

    // Get download link for completed report
    getDownloadLink: builder.query({
      query: (reportId) => ({
        url: `/reports/download-link/${reportId}`,
        method: 'GET',
      }),
      transformResponse: (response) => {
        return response.data || { url: null, expires_at: null };
      },
      // Download links can be cached briefly
      keepUnusedDataFor: 60, // 1 minute
    }),

    // Delete generated report
    deleteReport: builder.mutation({
      query: (reportId) => ({
        url: `/reports/${reportId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, reportId) => [
        { type: 'Report', id: reportId }
      ],
    }),

    // Get report usage statistics
    getReportUsageStats: builder.query({
      query: (params = {}) => ({
        url: '/reports/usage-stats',
        method: 'GET',
        params: {
          client_id: params.client_id,
          period: params.period || '30d',
          group_by: params.group_by || 'user', // user, template, format
        },
      }),
      transformResponse: (response) => {
        return response.data || { usage: [], summary: {} };
      },
      keepUnusedDataFor: 300, // 5 minutes
    }),
  }),
});

// Export hooks for use in components
export const {
  // Queries
  useGetReportStatusQuery,
  useGetReportTemplatesQuery,
  useGetReportHistoryQuery,
  useGetReportConfigQuery,
  useGetDownloadLinkQuery,
  useGetReportUsageStatsQuery,
  
  // Mutations
  useGenerateReportMutation,
  useGenerateBatchReportMutation,
  useDownloadReportMutation,
  useBatchDownloadRecordingsMutation,
  useExportRecordingsMutation,
  useExportDashboardDataMutation,
  useCancelReportJobMutation,
  usePreviewReportMutation,
  useDeleteReportMutation,
  
  // Lazy queries (for manual triggering)
  useLazyGetReportStatusQuery,
  useLazyGetReportHistoryQuery,
  useLazyGetDownloadLinkQuery,
  
  // For manual cache invalidation if needed
  util: { invalidateTags },
} = reportApi;

export default reportApi;